import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Dropdown,
  Menu,
  Popover,
  Badge,
  Form,
} from "antd";
import {
  MoreOutlined,
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getSalesOrders, deleteSalesOrder } from "../../../redux/actions/sale/salesOrderAction";
import { loadAllCustomersForSearch } from "../../../redux/actions/customer/getCustomerAction";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../../utils/dateFilterCache";

const { RangePicker } = DatePicker;
const { Option } = Select;

const SalesOrderList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, pagination, loading } = useSelector((state) => state.salesOrders);
  const allCustomers = useSelector((state) => state.customers.searchList || state.customers.list) || [];

  const [filters, setFilters] = useState(() => {
    const c = getCachedDateFilter(CACHE_KEYS.SALES_ORDER_LIST);
    return {
      status: "",
      customer_id: "",
      start_date: c?.startdate ?? "",
      end_date: c?.enddate ?? "",
      page: 1,
      limit: 20,
    };
  });
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterForm] = Form.useForm();

  useEffect(() => {
    dispatch(getSalesOrders(filters));
    dispatch(loadAllCustomersForSearch({ status: true }));
  }, [dispatch, filters]);

  const handleFilterOpenChange = (open) => {
    setFilterVisible(open);
    if (open) {
      filterForm.setFieldsValue({
        status: filters.status || undefined,
        customer_id: filters.customer_id || undefined,
        dateRange: filters.start_date && filters.end_date
          ? [moment(filters.start_date), moment(filters.end_date)]
          : undefined,
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this sales order?")) {
      const result = await dispatch(deleteSalesOrder(id));
      if (result?.message === "success") {
        dispatch(getSalesOrders(filters));
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      partial: "blue",
      fulfilled: "green",
      cancelled: "red",
    };
    return colors[status] || "default";
  };

  const handleCreatePurchaseOrder = (orderId) => {
    navigate(`/purchase-order/add?sales_order_id=${orderId}`);
  };

  const handleCreateSaleInvoice = (orderId) => {
    navigate(`/sale?sales_order_id=${orderId}`);
  };

  const hasActiveFilter = filters.status || filters.customer_id || filters.start_date || filters.end_date;

  const handleFilterApply = (values) => {
    const dateRange = values.dateRange;
    const start_date = dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "";
    const end_date = dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "";
    if (start_date && end_date) setCachedDateFilter(CACHE_KEYS.SALES_ORDER_LIST, { startdate: start_date, enddate: end_date });
    setFilters((prev) => ({
      ...prev,
      status: values.status || "",
      customer_id: values.customer_id || "",
      start_date,
      end_date,
      page: 1,
    }));
    setFilterVisible(false);
  };

  const handleFilterClear = () => {
    filterForm.resetFields();
    setFilters({
      status: "",
      customer_id: "",
      start_date: "",
      end_date: "",
      page: 1,
      limit: filters.limit,
    });
    setFilterVisible(false);
  };

  const filterContent = (
    <div style={{ width: 280 }}>
      <Form
        form={filterForm}
        layout="vertical"
        onFinish={handleFilterApply}
      >
        <Form.Item name="status" label="Status">
          <Select placeholder="All statuses" allowClear>
            <Option value="pending">Pending</Option>
            <Option value="partial">Partial</Option>
            <Option value="fulfilled">Fulfilled</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </Form.Item>
        <Form.Item name="customer_id" label="Customer">
          <Select
            placeholder="All customers"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children ?? "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {allCustomers.map((customer) => (
              <Option key={customer.id} value={customer.id}>
                {customer.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="dateRange" label="Date Range">
          <RangePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button onClick={handleFilterClear}>Clear</Button>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
            Apply
          </Button>
        </Space>
      </Form>
    </div>
  );

  const columns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (text, record) => (
        <a onClick={() => navigate(`/sales-order/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (customer) => customer?.name || "-",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Items",
      key: "items",
      render: (_, record) => record.order_items?.length || 0,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="view"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/sales-order/${record.id}`)}
            >
              View Details
            </Menu.Item>
            {record.status !== "fulfilled" && record.status !== "cancelled" && (
              <>
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/sales-order/edit/${record.id}`)}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  key="purchase"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => handleCreatePurchaseOrder(record.id)}
                >
                  Create to Purchase
                </Menu.Item>
                <Menu.Item
                  key="invoice"
                  icon={<FileTextOutlined />}
                  onClick={() => handleCreateSaleInvoice(record.id)}
                >
                  Create to Sales
                </Menu.Item>
              </>
            )}
            {record.status !== "fulfilled" && (
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
              >
                Delete
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <span
              className="ant-dropdown-trigger"
              style={{
                cursor: "pointer",
                padding: "4px 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0, 0, 0, 0.85)",
                fontSize: 18,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="card card-custom list-page-fill-viewport">
      <div className="card-body">
        <div className="list-page-header" style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <h2 style={{ margin: 0 }}>Sales Orders</h2>
          <Space wrap>
            <Popover
              content={filterContent}
              title="Filters"
              trigger="click"
              open={filterVisible}
              onOpenChange={handleFilterOpenChange}
              placement="bottomRight"
            >
              <Badge count={hasActiveFilter ? 1 : 0} size="small" offset={[-2, 2]}>
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Badge>
            </Popover>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/sales-order/add")}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: 600 }}
            >
              Add Sales Order
            </Button>
          </Space>
        </div>

        {/* Table */}
        <div className="list-page-table-container">
          <div className="list-page-table-inner">
            <div className="list-page-table-wrap">
              <Table
                columns={columns}
                dataSource={list || []}
                rowKey="id"
                loading={loading}
                scroll={{ x: true, y: 450 }}
                pagination={{
                  current: filters.page,
                  total: pagination?.total || 0,
                  pageSize: filters.limit,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    setFilters((prev) => ({
                      ...prev,
                      page,
                      limit: pageSize,
                    }));
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesOrderList;
